// Admin audit log page — cross-tenant read-only view for super-admins.
// Filters: tenant_id, resource_type, action prefix.

import { useState } from "react";
import type { JSX, ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminFetch } from "../lib/api.js";
import { AdminNav } from "../components/AdminNav.js";

interface AuditEntry {
  _id:           string;
  tenant_id:     string;
  actor_id:      string;
  actor_role:    string;
  action:        string;
  resource_type: string;
  resource_id:   string;
  ip_address:    string;
  created_at:    string;
}

interface AuditMeta { page: number; limit: number; total: number; }
interface AuditListResponse { data: AuditEntry[]; meta: AuditMeta; }

function fmtTs(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
}

function buildUrl(filters: Record<string, string>, page: number): string {
  const params = new URLSearchParams({ page: String(page), limit: "25" });
  for (const [k, v] of Object.entries(filters)) {
    if (v.trim()) params.set(k, v.trim());
  }
  return `/admin/audit-logs?${params.toString()}`;
}

function AuditLogTable({ entries }: { entries: AuditEntry[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
            <th className="py-3 px-4 font-medium">Timestamp</th>
            <th className="py-3 pr-4 font-medium">Tenant</th>
            <th className="py-3 pr-4 font-medium">Actor</th>
            <th className="py-3 pr-4 font-medium">Action</th>
            <th className="py-3 pr-4 font-medium">Resource</th>
            <th className="py-3 pr-4 font-medium">IP</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {entries.map((e) => (
            <tr key={e._id} className="hover:bg-slate-50 transition-colors">
              <td className="py-3 px-4 text-slate-500 whitespace-nowrap font-mono text-xs">{fmtTs(e.created_at)}</td>
              <td className="py-3 pr-4 font-mono text-xs text-slate-500">{e.tenant_id.slice(-8)}</td>
              <td className="py-3 pr-4 text-xs text-slate-700">
                <span className="font-mono">{e.actor_id.slice(-8)}</span>
                <span className="ml-1 text-slate-400">({e.actor_role})</span>
              </td>
              <td className="py-3 pr-4">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 font-medium">{e.action}</span>
              </td>
              <td className="py-3 pr-4 text-xs text-slate-600">
                {e.resource_type}
                {e.resource_id && <span className="ml-1 text-slate-400">#{e.resource_id.slice(-6)}</span>}
              </td>
              <td className="py-3 pr-4 font-mono text-xs text-slate-400">{e.ip_address}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminAuditLogsPage(): JSX.Element {
  const [tenantId,     setTenantId]     = useState("");
  const [resourceType, setResourceType] = useState("");
  const [action,       setAction]       = useState("");
  const [page,         setPage]         = useState(1);
  const [active, setActive] = useState({ tenantId: "", resourceType: "", action: "" });

  function search(): void { setActive({ tenantId, resourceType, action }); setPage(1); }

  const url = buildUrl({ tenant_id: active.tenantId, resource_type: active.resourceType, action: active.action }, page);
  const { data, isLoading, isError } = useQuery<AuditListResponse>({
    queryKey: ["admin-audit-logs", active, page],
    queryFn:  () => adminFetch<AuditListResponse>(url),
  });

  const entries: AuditEntry[] = data ? data.data : [];
  const total = data ? data.meta.total : 0;
  const pages   = Math.max(1, Math.ceil(total / 25));
  const ready   = !isLoading && !isError;

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <h1 className="text-xl font-semibold text-slate-900">Audit Logs</h1>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex flex-wrap gap-3">
            <FilterInput label="Tenant ID" value={tenantId} onChange={setTenantId} placeholder="uuid" />
            <FilterInput label="Resource"  value={resourceType} onChange={setResourceType} placeholder="patient" />
            <FilterInput label="Action"    value={action}       onChange={setAction}       placeholder="patient.update" />
            <div className="flex items-end">
              <button onClick={search} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700">Search</button>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {isLoading && <p className="py-10 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>}
          {isError   && <p role="alert" className="py-10 text-center text-sm text-red-600">Failed to load audit logs.</p>}
          {ready && entries.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No entries found.</p>}
          {ready && entries.length > 0   && <AuditLogTable entries={entries} />}
        </div>
        <Pagination page={page} pages={pages} total={total} onPageChange={setPage} />
      </div>
    </div>
  );
}

interface PaginationProps { page: number; pages: number; total: number; onPageChange: (fn: (p: number) => number) => void; }

function Pagination({ page, pages, total, onPageChange }: PaginationProps): JSX.Element {
  if (pages <= 1) return <></>;
  return (
    <div className="flex items-center justify-between text-sm text-slate-500">
      <span>{total} entries</span>
      <div className="flex gap-2">
        <button onClick={() => onPageChange((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Prev</button>
        <span className="px-2 py-1">{page} / {pages}</span>
        <button onClick={() => onPageChange((p) => Math.min(pages, p + 1))} disabled={page === pages} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Next</button>
      </div>
    </div>
  );
}

function FilterInput({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-40">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}
